import { BillingPeriod } from '../model/BillingPeriod';
import request from './request';
import { getAccessToken, getAccountId, isUserAuthenticated } from './helpers';
import { createErrorItem } from './error';
import { MockApi } from './mockApi';
import { Account } from '../model/Account';
import { Subscription } from '../model/Subscription';
import { Products } from '../model/Products';
import { SimCard } from '../model/SimCard';
import { Order } from '../model/Order';
import { Invoice } from '../model/Invoice';
import { Usage } from '../model/Usage';
import { Authentication } from '../model/Authentication';
import { Notification } from '../model/Notification';
import { OfferingType } from './offeringType';
import { Restriction } from '../model/restriction';
import {AccountOwner} from "../model/AccountOwner";
import {TransferDatesResponse} from "./mnpTransferDateService";

const USE_MOCKS = process.env.REACT_APP_USE_MOCKS === 'true';

const backendHost = `//${window.location.hostname}`;
const API_ROOT = process.env.REACT_APP_API_PROXY_URL || process.env.REACT_APP_API_URL || `${backendHost}/yrityksille/api`;

const SYSTEM = 'MUNMOI_WEB';

const REQUEST_TYPE_GET = 'GET';
const REQUEST_TYPE_POST = 'POST';

const mockApi = USE_MOCKS ? new MockApi() : undefined;

export class ApiRequest<T> {
	requestType: string;
	endpointAndQueryParameters: string;
	data: any;
	headers: any;
	requireAuthorization: boolean;

	constructor(requestType: string, endpointAndQueryParameters: string, data = {}, headers = {}, requireAuthorization = true) {
		this.requestType = requestType;
		this.endpointAndQueryParameters = endpointAndQueryParameters;
		this.data = data;
		this.headers = headers;
		this.requireAuthorization = requireAuthorization;
	}

	execute(): Promise<T> {
		return this.fetchJson()
			.catch((error: any) => {
				console.log(error);
				if (this.requireAuthorization && error.response && error.response.status === 401) {
					if (isUserAuthenticated()) {
						// We have a local access token, but the server reported that the user is not authenticated
						// -> most likely the session has expired in the backend and we should log out the user
						ApiRequest.logoutUnauthorized();
					}
				}
				return Promise.reject(createErrorItem(error.response, this.endpointAndQueryParameters));
			})
			.then((response: any) => {
				if (response.error) {
					// response.status !== 401
					return Promise.reject(createErrorItem(response, this.endpointAndQueryParameters));
				} else {
					return response.data ? response.data : response;
				}
			});
	}

	static logoutUnauthorized() {
		// TODO do not duplicate the logout functionality here (execute USER_LOGOUT action from application code instead, if possible)
		ApiRequest.logout();
		sessionStorage.removeItem('accessToken');
		sessionStorage.removeItem('accountId');
		sessionStorage.removeItem('userId');
		sessionStorage.removeItem('customerType');
		window.location.reload();
	}

	static getAuthorizationHeader() {
		const token = getAccessToken();
		if (token !== null) {
			return `Bearer ${token}`;
		} else {
			return undefined;
		}
	}

	static getRequestOptions(requestType: string, data: any, headers: any, requireAuthorization: boolean) {
		const options: any = {
			headers: {
				'Content-Type': 'application/json',
				'fi-mobileoperator-system': SYSTEM,
				...headers
			}
		};
		if (requestType === REQUEST_TYPE_POST) {
			options.method = REQUEST_TYPE_POST;
			options.body = JSON.stringify(data);
		}
		if (requireAuthorization) {
			options.headers.Authorization = ApiRequest.getAuthorizationHeader();
		}
		return options;
	}

	fetchJson(): any {
		if (!USE_MOCKS) {
			const fullUrl = API_ROOT + this.endpointAndQueryParameters;
			return request(fullUrl, ApiRequest.getRequestOptions(this.requestType, this.data, this.headers, this.requireAuthorization));
		} else {
			if (this.requestType === REQUEST_TYPE_POST) {
				console.log('Post data for ' + this.endpointAndQueryParameters);
				console.log(JSON.stringify(this.data, undefined, '\t'));
			}
			return mockApi!.getUrl(this.endpointAndQueryParameters, this.data).then((url) => request(url, ApiRequest.getRequestOptions(REQUEST_TYPE_GET, undefined, this.headers, false)));
		}
	}

	static login(username: any, password: any) {
		return new ApiRequest<Authentication>(REQUEST_TYPE_POST, '/login', { username, password }, undefined, false);
	}

	static logout() {
		return new ApiRequest<{}>(REQUEST_TYPE_POST, '/logout');
	}

	static getNotifications() {
		return new ApiRequest<{ notifications: Notification[] }>(REQUEST_TYPE_GET, '/notifications', {}, {}, false);
	}

	// billing

	// TODO: bug: initializeInvoices fetching in invoices page fail for some reason (id is undefined), fixed now by not using id
	static getBillingPeriods(id: any) {
		return new ApiRequest<BillingPeriod[]>(REQUEST_TYPE_GET, `/billing-periods/${getAccountId()}`); // eslint-disable-line
	}

	static getInvoices(accountId: any, fromBillingPeriodId: any, amount: any) {
		return new ApiRequest<Invoice[]>(REQUEST_TYPE_GET, `/invoices/${accountId}/${fromBillingPeriodId}/${amount}`); // eslint-disable-line
	}

	static getInvoicePdf(accountId: any, invoiceId: any, invoiceNumber: any) {
		return new ApiRequest<any>(REQUEST_TYPE_GET, `/invoice-attachment-pdf/${accountId}/${invoiceId}/${invoiceNumber}`, undefined, {
			'Content-Type': 'application/pdf',
			Accept: 'application/pdf'
		});
	}

	static getInvoiceExcel(accountId: any, invoiceNumber: any, billingPeriodId: any) {
		return new ApiRequest<any>(REQUEST_TYPE_GET, `/invoice-attachment-excel/${accountId}/${invoiceNumber}/${billingPeriodId}`, undefined, {
			'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		});
	}

	static getZuoraSignature(pageId: any, zuoraUrl: string | number | boolean) {
		return new ApiRequest<any>(REQUEST_TYPE_GET, `/zuora-token/?pageId=${pageId}&uri=${encodeURIComponent(zuoraUrl)}&url=${encodeURIComponent(zuoraUrl)}`);
	}

	static setDefaultPaymentMethod(accountId: any, paymentMethodId: any) {
		return new ApiRequest<{}>(REQUEST_TYPE_POST, `/change-default-payment-method/${accountId}`, { paymentMethodId });
	}

	// customer

	static loadCustomerData(id: any) {
		return new ApiRequest<Account>(REQUEST_TYPE_GET, `/account/${id}`);
	}

	static updateCustomer(data: any, id: any) {
		return new ApiRequest<Account>(REQUEST_TYPE_POST, `/accounts/update/${id}`, data);
	}

	// emptySims

	static getEmptySims(id: any) {
		return new ApiRequest<SimCard[]>(REQUEST_TYPE_GET, `/sims/empty/account/${id}`);
	}

	static getFreeNumbers() {
		return new ApiRequest<{ msisdn: string }[]>(REQUEST_TYPE_GET, '/free-numbers');
	}

	static activateConsumerSubscription(data: any, subscriptionNumber: any) {
		return new ApiRequest<{}>(REQUEST_TYPE_POST, `/subscriptions/activate/${subscriptionNumber}`, data);
	}

	static activateBusinessSubscription(data: any) {
		return new ApiRequest<{}>(REQUEST_TYPE_POST, '/subscriptions/create-and-activate', data);
	}

	static switchSimCard(subscriptionNumber: any, simId: any, password: any) {
		return new ApiRequest<Subscription>(
			REQUEST_TYPE_POST,
			`/subscriptions/update/${subscriptionNumber}`,
			{updates: [{field: "simId", value: simId}], password }
		);
	}

	static getAbleToActivate(subscriptionNumber: any) {
		return new ApiRequest<boolean>(REQUEST_TYPE_GET, `/subscriptions/able-to-activate/${subscriptionNumber}`);
	}

	// mnp transferdates
	static getTransferDates() {
		return new ApiRequest<TransferDatesResponse>(REQUEST_TYPE_GET, '/mnp-transfer-date-choices');
	}

	// forgotPassword

	static resetPassword(email: any, url: any) {
		return new ApiRequest<{}>(REQUEST_TYPE_POST, '/send-password-email', { email, url }, undefined, false);
	}

	static sendNewPassword(token: any, password: any) {
		return new ApiRequest<{}>(REQUEST_TYPE_POST, `/set-password-token?token=${token}`, { password }, undefined, false);
	}

	static updatePassword(id: string, password: string, oldPassword: string) {
		return new ApiRequest<{}>(REQUEST_TYPE_POST, `/set-password?userId=${encodeURIComponent(id)}`, { password, oldPassword });
	}

	// products
	static getProducts(offeringType: OfferingType) {
		return new ApiRequest<Products>(REQUEST_TYPE_GET, `/sales-products/${offeringType}`, {}, {}, false);
	}

	// simCards

	static orderSims(id: any, data: any) {
		return new ApiRequest<{}>(REQUEST_TYPE_POST, `/sims/order/${id}`, data);
	}

	static orderSubscriptions(id: any, data: any) {
		return new ApiRequest<{}>(REQUEST_TYPE_POST, `/subscriptions/order/${id}`, data);
	}

	// sims

	static getSims(id: any, includeSettings?: boolean, excludeTerminating?: boolean) {
		const settingsIncluded = includeSettings !== undefined ? includeSettings : false;
		const terminatingExcluded = excludeTerminating !== undefined ? excludeTerminating : false;
		return new ApiRequest<Subscription[]>(REQUEST_TYPE_GET, `/subscriptions/account/${id}?status=active&excludeTerminating=${terminatingExcluded}&includeSettings=${settingsIncluded}`);
	}

	static updateSim(id: any, data: any) {
		return new ApiRequest<Subscription>(REQUEST_TYPE_POST, `/options/update/${id}`, data);
	}

	static updateSimInfo(id: any, data: any) {
		return new ApiRequest<Subscription>(REQUEST_TYPE_POST, `/subscriptions/update/${id}`, data);
	}

	static updateOrder(data: any) {
		return new ApiRequest<{}>(REQUEST_TYPE_POST, `/order/update/${getAccountId()}`, data);
	}

	static submitNumsi(subscriptionNumber: any, data: any) {
		return new ApiRequest<{}>(REQUEST_TYPE_POST, `/subscriptions/mnp-info/${subscriptionNumber}`, data);
	}

	static getCurrentOrder(id: any) {
		return new ApiRequest<Order>(REQUEST_TYPE_GET, `/order/${id}`);
	}

	static getCurrentSim(id: any) {
		return new ApiRequest<Subscription>(REQUEST_TYPE_GET, `/subscriptions/${id}`);
	}

	static getNonActivatedSims(id: any, excludeTerminating?: boolean) {
		const terminatingExcluded = excludeTerminating !== undefined ? excludeTerminating : false;
		return new ApiRequest<Subscription[]>(REQUEST_TYPE_GET, `/subscriptions/account/${id}?status=new&excludeTerminating=${terminatingExcluded}`);
	}

	// usage

	static getAllUsages(id: any, startDate: any, endDate: any) {
		return new ApiRequest<Usage>(REQUEST_TYPE_GET, `/detailed-usages?accountNumber=${id}&startDate=${startDate}&endDate=${endDate}`);
	}

	static getSubscriptionUsage(id: any, startDate: any, endDate: any) {
		return new ApiRequest<Usage>(REQUEST_TYPE_GET, `/detailed-subscription-usage?subscriptionNumber=${id}&startDate=${startDate}&endDate=${endDate}`);
	}

	// offeringChange

	static offeringChange(data: any) {
		return new ApiRequest<{}>(REQUEST_TYPE_POST, `/account/${getAccountId()}/offering-change`, data);
	}

	// Restrictions

	static getRestrictions(id: any) {
		return new ApiRequest<Restriction[]>(REQUEST_TYPE_GET, `/account/${id}/restrictions`);
	}

	static logError(data: any) {
		return new ApiRequest<{}>(REQUEST_TYPE_POST, '/error-log/', data);
	}

	static getAccountOwner(id: any) {
		return new ApiRequest<AccountOwner>(REQUEST_TYPE_GET, `/accounts/${id}/orderer-owner`);
	}
}
